import { amorphicStatic, supertypeClass } from '@haventech/amorphic';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../../common/js/logger/LoggerSingleton';
import { Customer } from '../../../common/js/customer/Customer';
import { CustomerUtils } from '../../../common/js/customer/CustomerUtils';
import { CoverpathCustomer } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathCustomer';
import { serverOnly } from '../../../common/js/helpers';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../../../common/js/logger/UILoggerSingleton';

@supertypeClass({ toClient: false, toServer: false })
export class CoverpathCustomerUtils extends CustomerUtils {
  public static async customerHasServicingAgent(email: string, sessionCustomer: Customer): Promise<boolean> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'Controller',
        function: 'customerHasServicingAgent',
        context: {
          piiLevel: UIPiiLevelEnum.s2,
        },
        data: {
          email,
          hasSessionCustomer: !!sessionCustomer,
        },
        category: UICategory.request,
      },
      'Invoking customerHasServicingAgent'
    );

    // Check session
    let hasAgent: boolean = this.hasPrimaryAdvisor(sessionCustomer);

    if (!hasAgent) {
      const matchingRecords = await CoverpathCustomer.getKnex().client.raw(
        `
                SELECT c."_id", p."_id", p."primaryPolicyAgent_id"
                FROM policy p
                INNER JOIN customer c on p.customer_id = c."_id"
                WHERE c."email" = ?
                AND p."primaryPolicyAgent_id" IS NOT NULL
            `,
        [email]
      );

      UILoggerSingleton.getInstance().info(
        {
          module: 'Controller',
          function: 'customerHasServicingAgent',
          data: {
            recordCount: matchingRecords ? matchingRecords.rowCount : 0,
          },
          category: UICategory.request,
        },
        'No agent in session. Checking persisted Customer'
      );

      hasAgent = matchingRecords && matchingRecords.rowCount && matchingRecords.rowCount > 0;
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'Controller',
        function: 'customerHasServicingAgent',
        data: {
          hasAgent: !!hasAgent,
        },
        category: UICategory.request,
      },
      'Checked if customer has servicing agent'
    );

    return hasAgent;
  }

  static async customerHasOnlyDraftInProcess(email: string): Promise<boolean> {
    const matchingRecords = await CoverpathCustomer.getKnex().client.raw(
      ` SELECT pol."linkSentDate", app."isAgentAssistant"
        FROM policy pol
        INNER JOIN application app on app._id = pol.application_id
        INNER JOIN customer cust on cust._id = app.customer_id
        WHERE cust."email" = ?
        AND app."type" = 'active'
    `,
      [email]
    );

    if (!matchingRecords || (matchingRecords && matchingRecords.rowCount === 0)) {
      return false;
    }
    const allAppsCount = matchingRecords.rows.length;
    const draftApps = matchingRecords.rows.filter((x) => x.isAgentAssistant && x.linkSentDate === null);
    return allAppsCount === draftApps.length;
  }

  static async customerHasApplicationAccess(
    customer: Customer,
    applicationNumber: string,
    session: any
  ): Promise<boolean> {
    const matchingRecords = await CoverpathCustomer.getKnex().client.raw(
      `
            SELECT app."type"
            FROM application app, policy pol
            where app."_id" = pol."application_id"
            AND app."type" = 'active'
            AND pol."type" = 'primary'
            AND (pol."linkSentDate" is NOT null OR coalesce(app."isAgentAssistant", false) = 'false')
            AND app."customer_id" = ?
            AND app."applicationNumber" = ?
        `,
      [customer._id, applicationNumber]
    );

    if (matchingRecords && matchingRecords.rowCount) {
      return matchingRecords.rowCount > 0;
    }

    UILoggerSingleton.getInstance().warn(
      {
        module: 'Controller',
        function: 'customerHasApplicationAccess',
        data: {
          applicationNumber,
        },
        category: UICategory.request,
      },
      `Failed to fetch Application type using customer_id=${customer.__id__} and applicationNumber=${applicationNumber}`
    );

    return false;
  }

  @serverOnly()
  public static async getCustomerByEmailCaseInsensitive(email: string): Promise<CoverpathCustomer | null> {
    const customerIdResultSet = await CoverpathCustomer.getKnex().client.raw(
      `select "_id" from customer where "_template" = 'CoverpathCustomer' and lower(email) = lower(?)`,
      [email.toLowerCase().trim()]
    );
    if (customerIdResultSet.rows.length === 0) {
      return null;
    }
    const customerId = customerIdResultSet.rows[0]['_id'];
    const idCount = customerIdResultSet.rows.length;
    if (idCount > 1) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'controller.getCustomerByEmailCaseInsensitive',
          activity: 'getCustomerByEmailCaseInsensitive',
        },
        `${idCount} emails found during case insensitive email search for customer "${customerId}"`
      );
    }

    const query = {
      _id: customerId,
      _template: 'CoverpathCustomer',
    };
    const customers = await CoverpathCustomer.persistorFetchByQuery(query, {
      fetch: {
        insuranceQuotes: false,
        applications: false,
        policies: false,
      },
    });
    return customers[0];
  }
}
